// Pure, dependency-free projection of GeoJSON polygons into SVG path strings.
//
// One shared projection (baked once at generation time and reused at runtime)
// guarantees that the Alabama county backdrop and any overlay — a selected
// city or an uploaded custom area — line up exactly in the same viewBox.
//
// The projection is a simple equirectangular mapping with a latitude
// correction (longitude degrees are scaled by cos(midLat)) so Alabama doesn't
// look horizontally stretched. A single uniform scale is applied to both axes,
// so shapes keep their proportions.

// Projects one [lng, lat] coordinate into SVG user units.
export const projectPoint = ([lng, lat], projection) => {
  const {
    minLng, maxLat, cosLat, scale, padding,
  } = projection;
  const x = padding + (lng - minLng) * cosLat * scale;
  const y = padding + (maxLat - lat) * scale;
  return [x, y];
};

// Inverse of projectPoint: maps an SVG [x, y] back to [lng, lat]. Useful for
// deriving geographic coordinates (e.g. a sample tile grid) from baked,
// already-projected paths without shipping the raw GeoJSON to the client.
export const unprojectPoint = ([x, y], projection) => {
  const {
    minLng, maxLat, cosLat, scale, padding,
  } = projection;
  const lng = minLng + (x - padding) / (cosLat * scale);
  const lat = maxLat - (y - padding) / scale;
  return [lng, lat];
};

// 2 decimals keeps baked path strings compact without visible loss at the
// sizes this SVG renders.
const formatCoordinate = (n) => Number(n.toFixed(2));

const ringToPath = (ring, projection) => {
  const commands = ring.map((coord, index) => {
    const [x, y] = projectPoint(coord, projection);
    return `${index === 0 ? 'M' : 'L'}${formatCoordinate(x)} ${formatCoordinate(y)}`;
  });
  return `${commands.join(' ')} Z`;
};

// Normalizes any GeoJSON shape down to a flat list of polygons, where each
// polygon is an array of rings (outer ring first, then holes).
const collectPolygons = (geometry) => {
  if (!geometry || !geometry.type) return [];
  switch (geometry.type) {
    case 'Polygon':
      return [geometry.coordinates];
    case 'MultiPolygon':
      return geometry.coordinates;
    case 'GeometryCollection':
      return (geometry.geometries || []).flatMap(collectPolygons);
    case 'Feature':
      return collectPolygons(geometry.geometry);
    case 'FeatureCollection':
      return (geometry.features || []).flatMap((f) => collectPolygons(f.geometry));
    default:
      return [];
  }
};

// Accepts GeoJSON as either a parsed object or a JSON string (the server
// returns simplified outlines as strings) and returns the parsed geometry,
// or null if it can't be parsed.
export const toGeometry = (geoJson) => {
  if (!geoJson) return null;
  if (typeof geoJson === 'string') {
    try { return JSON.parse(geoJson); } catch { return null; }
  }
  return geoJson;
};

// Converts a GeoJSON Polygon/MultiPolygon (or wrapping Feature/collection)
// into a single SVG path `d` string covering every ring. Returns '' for
// anything with no polygonal content.
export const geometryToSvgPath = (geoJson, projection) => {
  const geometry = toGeometry(geoJson);
  const polygons = collectPolygons(geometry);
  return polygons
    .flatMap((rings) => rings.map((ring) => ringToPath(ring, projection)))
    .join(' ');
};

// Extracts the projected bounds {minX, minY, maxX, maxY} from an SVG path `d`
// string of the kind this module (and the baked county paths) produce: absolute
// M/L commands followed by numeric "x y" pairs, closed with Z. Because every
// command emits coordinates in x/y pairs, the numeric tokens can be paired off
// directly. Returns null when the string has no coordinates.
export const svgPathBounds = (d) => {
  if (!d) return null;
  const nums = d.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length < 2) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = Number(nums[i]);
    const y = Number(nums[i + 1]);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return {
    minX, minY, maxX, maxY,
  };
};

// Unions two bounds boxes (either may be null). Returns null when both are null.
export const mergeBounds = (a, b) => {
  if (!a) return b ?? null;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
};
